import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { AlertTriangle, Check } from "lucide-react"
import type { ConformityV2Framework } from "../../../api/conformityApi"

const SAMPLE_CAP = 5

/** General scope only (TUR-1573, design section 6): the contradiction
 *  guardrail. One row per framework whose master data contradicts its
 *  knowledge-based statement, with the three resolutions (untick, disclose,
 *  acknowledge). Renders nothing when nothing contradicts. Visual language
 *  (warning border, AlertTriangle, `text-[12.5px]`) follows `GapList.tsx`. */
export function GeneralWarningPanel({
  frameworks,
  onUntick,
  onDisclose,
  onAcknowledge,
}: {
  frameworks: ConformityV2Framework[]
  /** Untick and disclose are guarded on MC_CUSTOMER_DECLARATIONS_GENERATE
   *  server-side (PATCH /frameworks/{key}, /disclosure). Undefined hides the
   *  action, the same mechanism as `onAcknowledge`: a view-only member keeps
   *  the warning but is never offered a click that can only fail. */
  onUntick?: (frameworkKey: string) => void
  onDisclose?: (frameworkKey: string) => void
  /** POST /general-acknowledge needs MC_CUSTOMER_DECLARATIONS_OVERRIDE.
   *  Undefined hides the action entirely (same mechanism as RefusedPanel's
   *  `onScipOverride`, fix round 1): a user without the permission would
   *  otherwise open the dialog, type a reason and only then learn on confirm
   *  that they were never allowed to. Untick and disclose need only GENERATE,
   *  so for a member who has it the panel is never a dead end. */
  onAcknowledge?: (frameworkKey: string) => void
}) {
  const { translate } = useTranslation()
  const rows = frameworks.filter(
    (f) =>
      f.contradictions &&
      (f.contradictions.svhcCount > 0 || f.contradictions.adverseCount > 0),
  )
  if (rows.length === 0) return null

  return (
    <div className="flex flex-col gap-2.5 rounded-xl border border-warning/60 p-3">
      <div className="flex items-center gap-2 text-[12.5px] font-semibold text-warning">
        <AlertTriangle size={14} />
        {translate("MC_CS_CONF_V2_GENERAL_WARN_TITLE")}
      </div>
      {rows.map((f) => (
        <GeneralWarningRow
          key={f.frameworkKey}
          framework={f}
          onUntick={onUntick ? () => onUntick(f.frameworkKey) : undefined}
          onDisclose={onDisclose ? () => onDisclose(f.frameworkKey) : undefined}
          onAcknowledge={
            onAcknowledge ? () => onAcknowledge(f.frameworkKey) : undefined
          }
        />
      ))}
    </div>
  )
}

function GeneralWarningRow({
  framework,
  onUntick,
  onDisclose,
  onAcknowledge,
}: {
  framework: ConformityV2Framework
  onUntick?: () => void
  onDisclose?: () => void
  onAcknowledge?: () => void
}) {
  const { translate } = useTranslation()
  const contradiction = framework.contradictions
  if (!contradiction) return null

  // A disclosure resolves the framework regardless of ack staleness (design
  // section 6.5.1: "disclosures are not blocked this way", the printed
  // sentence stays true even over stale data, unlike a silent acknowledgement).
  // So disclosureText is checked FIRST; only then does an outdated ack fall
  // back to an active warning.
  const resolvedByDisclosure = !!framework.disclosureText
  const resolvedByAck =
    !resolvedByDisclosure && !!framework.ackReason && !framework.ackOutdated
  const resolved = resolvedByDisclosure || resolvedByAck
  const ackOutdatedWarning =
    !resolvedByDisclosure && !!framework.ackReason && !!framework.ackOutdated

  const samples = contradiction.samples.slice(0, SAMPLE_CAP)

  return (
    <div
      className="flex flex-col gap-1.5 rounded-md border border-white/[0.07] px-2.5 py-2 text-[12.5px]"
      data-testid={`general-warning-${framework.frameworkKey}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-white">{framework.label}</span>
        {contradiction.svhcCount > 0 && (
          <span className="text-grey5">
            {translate("MC_CS_CONF_V2_GENERAL_WARN_SVHC", {
              n: contradiction.svhcCount,
            })}
          </span>
        )}
        {contradiction.adverseCount > 0 && (
          <span className="text-grey5">
            {translate("MC_CS_CONF_V2_GENERAL_WARN_ADVERSE", {
              n: contradiction.adverseCount,
            })}
          </span>
        )}
        <span className="flex-1" />
        {resolved && (
          <span className="inline-flex items-center gap-1 text-[11px] text-grey5">
            <Check size={11} />
            {resolvedByDisclosure
              ? translate("MC_CS_CONF_V2_GENERAL_RESOLVED_DISCLOSED")
              : translate("MC_CS_CONF_V2_GENERAL_RESOLVED_ACK")}
          </span>
        )}
      </div>

      {samples.length > 0 && !resolved && (
        <div className="flex flex-wrap gap-1.5">
          {samples.map((s) => (
            <span
              key={s.articleId}
              className="font-mono inline-flex items-center rounded-md border border-grey4 px-2 py-1 text-[11px] text-grey5"
            >
              {s.articleNumber ?? s.articleId.slice(0, 8)}
              {s.articleName ? ` · ${s.articleName}` : ""}
            </span>
          ))}
        </div>
      )}

      {ackOutdatedWarning && (
        <p className="flex items-start gap-1.5 text-[11.5px] text-warning">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          {translate("MC_CS_CONF_V2_GENERAL_ACK_OUTDATED")}
        </p>
      )}

      {resolvedByDisclosure && framework.disclosureOutdated && (
        <p className="text-[11px] text-grey5">
          {translate("MC_CS_CONF_V2_GENERAL_DISCLOSURE_OUTDATED_HINT")}
        </p>
      )}

      {!resolved && (onUntick || onDisclose || onAcknowledge) && (
        <div className="flex flex-wrap gap-1.5">
          {onUntick && (
            <button
              type="button"
              onClick={onUntick}
              className="rounded-md border border-grey4 px-2.5 py-1 text-[11.5px] font-medium text-grey5 hover:border-grey5 hover:text-white"
            >
              {translate("MC_CS_CONF_V2_GENERAL_WARN_UNTICK")}
            </button>
          )}
          {onDisclose && (
            <button
              type="button"
              onClick={onDisclose}
              className="rounded-md border border-grey4 px-2.5 py-1 text-[11.5px] font-medium text-grey5 hover:border-grey5 hover:text-white"
            >
              {translate("MC_CS_CONF_V2_GENERAL_WARN_DISCLOSE")}
            </button>
          )}
          {onAcknowledge && (
            <button
              type="button"
              onClick={onAcknowledge}
              className="rounded-md border border-grey4 px-2.5 py-1 text-[11.5px] font-medium text-grey5 hover:border-grey5 hover:text-white"
            >
              {translate("MC_CS_CONF_V2_GENERAL_WARN_ACK")}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
